import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ContainsSubscriptions } from '../../../../core/services/interfaces/contains-subscriptions';
import { UploadDAO } from '../../../../core/services/dao/uploadDAO';
import { getFadeInTransition } from '../../../../core/pure-functions/animations/ng-animations-provider';
import { unsubscribeFromAll } from '../../../../core/pure-functions/rx-helper';
import { combineLatest, Observable, Subscription } from 'rxjs';
import {
  FileSelectionError,
  FileType,
  getExtension,
  getNameWithoutExtension,
  validateExtension,
  getUniqueFileName,
  ImageType,
  MediaType,
  MultiUploadConfig,
  ResourceType,
  getImgSize,
  ImageMeta,
  fileLessThanMaxMb,
  vesselImageNeedsCropping,
  resizeImage,
} from '../../../../core/pure-functions/upload/image-upload-helper';
import { mergeMap } from 'rxjs/operators';
import { VesselImageDAO } from '../../../../core/services/dao/vesselImageDAO';
import { VesselImage } from 'anchor-shared/lib/model/entity/vesselImage';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';

@Component({
  selector: 'app-multi-file-upload',
  templateUrl: './multi-file-upload.component.html',
  styleUrls: ['./multi-file-upload.component.scss'],
  animations: [getFadeInTransition(500)],
})
export class MultiFileUploadComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  @Input()
  vesselId: number;

  @Input()
  vessel: Vessel;

  @Input()
  associationCreator: VesselImageDAO;

  @Input()
  currentSortOrderLevel: number;

  @Input()
  containerHelpText: string;

  /**
   * Height in px. Defaults to 200.
   */
  @Input()
  dragContainerHeight: number;

  /**
   * Array of ExtensionType for validation before upload.
   * Passing null/undefined or empty array will allow any extension to be uploaded.
   */
  @Input()
  allowedExtensions: ImageType[] | FileType[];

  /**
   * Defaults to MediaType.File.
   * This is because we crop images to create thumbnails.
   */
  @Input()
  allowedMediaType: MediaType;

  /**
   * This is the path that will be included after the organization's public id.
   * Defaults to undefined, which results in the file being uploaded in the
   * organizations default id s3 container.
   */
  @Input()
  specificBucketPath: string;

  @Input()
  config: MultiUploadConfig;

  @Output()
  fileCreatedPath = new EventEmitter<VesselImage>();

  /**
   * Emits the files left behind, that did not satisfy the constraints and were not uploaded.
   * File(s) too large. Please find a lower resolution photo and try again.
   * Reminder, the maximum photo upload size is 10MB.
   */
  @Output()
  bulkUploadComplete = new EventEmitter<BadFile[]>();

  @Output()
  fileSelectionError = new EventEmitter<FileSelectionError>();

  files: File[] = [];
  badFiles: BadFile[] = [];
  progressValue = 0;
  subs: Subscription[] = [];
  uploadInProgress = false;
  totalFilesLength: number;

  constructor(private uploadDAO: UploadDAO) {}

  ngOnInit() {
    this.setDefaults();
    this.validateComponentState();
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  onFilesDropped(fileList: FileList) {
    Array.from(fileList).forEach((file) => {
      this.files.push(file);
    });
    this.uploadAllFiles();
  }

  onFilesSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    event.stopPropagation();
    Array.from(target.files).forEach((file) => {
      this.files.push(file);
    });
    this.uploadAllFiles();
  }

  private async getValidFiles(): Promise<ImageMeta[]> {
    let validFiles: ImageMeta[] = [];
    if (this.files.length > this.config.maxFileSelection) {
      this.fileSelectionError.emit({
        message: 'Please select up to 30 files.',
      });
      return null;
    }
    let obs = [];
    this.files.forEach((item) => {
      obs.push(getImgSize(item));
    });
    const results: ImageMeta[] = await combineLatest<ImageMeta[]>(
      obs,
    ).toPromise();
    if (results) {
      results.forEach((result) => {
        if (fileLessThanMaxMb(result.originalFile, this.config.maxFileSizeMb)) {
          validFiles.push(result);
        } else {
          this.badFiles.push({
            file: result.originalFile,
            reason: 'large',
          });
        }
      });
    }
    return validFiles;
  }

  private async uploadAllFiles(): Promise<void> {
    const validFiles = await this.getValidFiles();
    if (validFiles && validFiles.length > 0) {
      if (this.allowedMediaType === MediaType.IMAGE) {
        console.log('ready to upload: ' + validFiles.length);
        this.continueImageTrack(validFiles);
      } else {
        console.log('no impl yet');
        // this.continueFileTrack();
      }
      this.files = [];
    } else {
      this.bulkUploadComplete.emit(this.badFiles);
      this.files = [];
      this.badFiles = [];
    }
  }

  private async continueImageTrack(files: ImageMeta[]): Promise<void> {
    this.uploadInProgress = true;
    this.progressValue = 4;
    this.totalFilesLength = files.length * 3;
    for (let imageMeta of files) {
      const ext = getExtension(imageMeta.originalFile.name);
      const fileName = `Anchor-Yacht-Rental-${getUniqueFileName(ext)}`;

      if (validateExtension(ext, this.allowedExtensions)) {
        this.incrementProgress();
        const imageUrl = await resizeImage(
          URL.createObjectURL(imageMeta.originalFile),
          1280,
          720,
        );
        const image = await fetch(imageUrl)
          .then((r) => r.blob())
          .then(
            (blobFile) => new File([blobFile], fileName, { type: 'image/png' }),
          );

        this.uploadPhotosAndCreateAssociation(
          image,
          fileName,
          vesselImageNeedsCropping(imageMeta),
        );

        // this.subs.push(combinedResize);
      } else {
        console.log('invalid image extension type');
      }
    }
  }

  private uploadPhotosAndCreateAssociation(
    file: File,
    fileName: string,
    needsCrop: boolean,
  ): void {
    const uploadSub = combineLatest([
      this.uploadDAO.uploadVesselImage(file, fileName, this.vesselId),
    ])
      .pipe(
        mergeMap((responses) => {
          this.incrementProgress();
          return this.associationCreator.createAssociationFor(this.vesselId, {
            url: responses[0].url,
            resourceKey: responses[0].key,
            thumbUrl: responses[0].url,
            thumbKey: responses[0].key,
            vesselId: this.vesselId,
            sortOrder: needsCrop ? null : ++this.currentSortOrderLevel,
            needsCrop: needsCrop,
          });
        }),
      )
      .subscribe(
        (createdAssociation) => {
          this.fileCreatedPath.emit(createdAssociation);
          this.incrementProgress();
        },
        (error1) => {
          console.log(error1);
          this.incrementProgress();
        },
      );
    this.subs.push(uploadSub);
  }

  private incrementProgress(): void {
    this.progressValue += (1 / this.totalFilesLength) * 100;
    if (this.progressValue > 99) {
      this.bulkUploadComplete.emit(this.badFiles);
      this.uploadInProgress = false;
      this.totalFilesLength = 0;
      this.progressValue = 0;
    }
  }

  private setDefaults(): void {
    if (!this.dragContainerHeight) {
      this.dragContainerHeight = 200;
    }
    if (!this.allowedMediaType) {
      this.allowedMediaType = MediaType.FILE;
    }
  }

  private validateComponentState(): void {
    if (!this.vesselId) {
      throw Error('Vessel Id is required for upload.');
    }
  }
}

export interface BadFile {
  file: File;
  reason: 'large' | 'small';
}
